export const getStatementHealthRequest = (projectId: number) => ({
	method: "statements/health",
	apiVersion: 2 as const,
	expectedProjectId: projectId,
	headers: { ignoreBusy: "true" },
	data: {},
});

export interface IStatementHealthEntry {
	id: number;
	status: string;
	from: string;
	to: string;
	net: number;
}

export interface IClientStatementHealth {
	clientId: number;
	asOf: string;
	stuckDays: number;
	stuck: (IStatementHealthEntry & { type: string; ageDays: number })[];
	overlapping: { first: IStatementHealthEntry; second: IStatementHealthEntry }[];
	unbilled: { days: number; from: string; to: string; net: number }[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isId = (value: unknown): value is number => isCount(value) && value > 0;
const isTimestamp = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value) && Number.isFinite(Date.parse(value));
const isPeriod = (from: unknown, to: unknown) => isTimestamp(from) && isTimestamp(to) && Date.parse(from) <= Date.parse(to);

const parseAmount = (value: unknown): number | null => {
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	if (typeof value !== "string" || !/^-?\d+(?:\.\d+)?$/.test(value)) return null;
	const amount = Number(value);
	return Number.isFinite(amount) ? amount : null;
};

const parseEntry = (id: unknown, status: unknown, from: unknown, to: unknown, rawNet: unknown): IStatementHealthEntry | null => {
	const net = parseAmount(rawNet);
	if (!isId(id) || typeof status !== "string" || !isTimestamp(from) || !isTimestamp(to) || !isPeriod(from, to) || net === null) return null;
	return { id, status, from, to, net };
};

export const parseClientStatementHealth = (value: unknown, clientId: number): IClientStatementHealth | null => {
	if (!isId(clientId) || !isRecord(value) || typeof value.as_of !== "string" || !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value.as_of)
		|| !Number.isFinite(Date.parse(`${value.as_of.replace(" ", "T")}Z`)) || !isCount(value.stuck_days)
		|| !isRecord(value.counts) || !Array.isArray(value.stuck) || !Array.isArray(value.overlapping) || !Array.isArray(value.unbilled_days)
		|| value.counts.stuck !== value.stuck.length || value.counts.overlapping !== value.overlapping.length || value.counts.unbilled_clients !== value.unbilled_days.length) return null;
	const result: IClientStatementHealth = { clientId, asOf: value.as_of, stuckDays: value.stuck_days, stuck: [], overlapping: [], unbilled: [] };
	for (const row of value.stuck) {
		if (!isRecord(row) || !isId(row.client_id) || typeof row.type !== "string" || !isCount(row.age_days)) return null;
		const entry = parseEntry(row.id, row.status, row.period_start, row.period_end, row.eur_net);
		if (!entry) return null;
		if (row.client_id === clientId) result.stuck.push({ ...entry, type: row.type, ageDays: row.age_days });
	}
	for (const row of value.overlapping) {
		if (!isRecord(row) || !isId(row.client_id)) return null;
		const first = parseEntry(row.id_a, row.status_a, row.start_a, row.end_a, row.net_a);
		const second = parseEntry(row.id_b, row.status_b, row.start_b, row.end_b, row.net_b);
		if (!first || !second) return null;
		if (row.client_id === clientId) result.overlapping.push({ first, second });
	}
	for (const row of value.unbilled_days) {
		if (!isRecord(row) || !isId(row.client_id) || !isCount(row.days) || !isTimestamp(row.from_date) || !isTimestamp(row.to_date)
			|| !isPeriod(row.from_date, row.to_date)) return null;
		const net = parseAmount(row.eur_net);
		if (net === null) return null;
		if (row.client_id === clientId) result.unbilled.push({ days: row.days, from: row.from_date, to: row.to_date, net });
	}
	return result;
};

export const formatStatementHealthTime = (value: string, locale: string) => new Intl.DateTimeFormat(locale, {
	timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
}).format(new Date(value));